import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RMStock() {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const { exporting, handleExport } = usePdfExport('/export/rm-stock-pdf', 'RM_Stock');

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/raw-material`, {
        
      });
      setStock(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to fetch stock');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="rm-stock-title">RAW MATERIAL STOCK</h1>
          <p className="text-sm text-text-muted mt-2">Real-time calculated stock levels</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="export-pdf-button"
            onClick={() => handleExport()}
            disabled={exporting || stock.length === 0}
            className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {exporting ? 'EXPORTING...' : 'EXPORT PDF'}
          </Button>
          <Button
            data-testid="refresh-stock-button"
            onClick={fetchStock}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            REFRESH
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-text-muted">Loading stock data...</div>
        ) : stock.length === 0 ? (
          <div className="p-12 text-center text-text-muted">No stock data available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Steel Grade</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Quantity (Weight)</th>
                </tr>
              </thead>
              <tbody>
                {stock.map((item, idx) => (
                  <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`rm-stock-row-${idx}`}>
                    <td className="p-4 align-middle">{item.steel_grade}</td>
                    <td className="p-4 align-middle">{item.section}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{item.qty_weight.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}